import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { adminLabel } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inputCls = "h-9 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-sm";

export function PlatformAdminsPanel() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/platform/admins").then(({ data }) => setAdmins(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/admins", form);
      toast.success(`${form.name || form.email} can now sign in at ${adminLabel()}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove Vinconex admin ${a.email}?`)) return;
    try {
      await api.delete(`/platform/admins/${a.id}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="platform-admins-panel" className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5 mb-6">
      <div className="flex items-center gap-2 font-heading font-bold"><ShieldCheck className="h-4 w-4 text-[#0A8CF2]" /> Vinconex admin logins</div>
      <p className="text-xs text-slate-400 mt-1 mb-4">Staff accounts sign in at <span className="font-mono-tech text-slate-300" data-testid="admin-console-url">{adminLabel()}</span>, see every dealership and can open any dashboard. They are separate from dealership logins.</p>
      <div className="space-y-2 mb-4">
        {admins.map((a) => (
          <div key={a.id} data-testid={`platform-admin-${a.id}`} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="font-semibold">{a.name}</span> <span className="text-slate-500">· {a.email}</span>
              {a.id === user?.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">you</span>}
              {a.dealer_id && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-mono-tech">also dealer admin</span>}
            </div>
            {a.id !== user?.id && (
              <button onClick={() => remove(a)} data-testid={`remove-admin-${a.id}`} className="text-slate-500 hover:text-red-400" title="Remove admin"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={add} className="grid sm:grid-cols-4 gap-2">
        <Input required data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} />
        <Input required type="email" data-testid="new-admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@vinconex.com" className={inputCls} />
        <Input required type="password" minLength={12} data-testid="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password (12+ chars)" className={inputCls} />
        <Button type="submit" disabled={busy} data-testid="add-admin-btn" className="h-9 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-3.5 w-3.5" /> Add admin</>}
        </Button>
      </form>
    </div>
  );
}
